import { Spacer } from "@hackthenorth/north";
import { Button, Text } from "north.js";
import React from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";

import { BufferLearnMoreLink } from "../../buffer-information";
import {
  BUFFER_MINUTES,
  TBufferedShift,
  TBufferShift,
} from "../../shared/utils/buffer";
import {
  formatVolunteerShiftDay,
  getVolunteerShiftHourAndMinute,
} from "../../shared/utils/date";

export type TSignupConfirmShift = {
  shiftId: number;
  name: string;
  location?: string | null;
  start: Date;
  end: Date;
};

type Props = {
  open: boolean;
  shift: TSignupConfirmShift | null;
  projected: TBufferedShift | undefined;
  neighborsLosingBuffer: TBufferShift[];
  onConfirm: () => void;
  onCancel: () => void;
};

const SignupConfirmModal: React.FC<Props> = ({
  open,
  shift,
  projected,
  neighborsLosingBuffer,
  onConfirm,
  onCancel,
}) => {
  if (!shift) return null;

  const startStr = getVolunteerShiftHourAndMinute(shift.start);
  const endStr = getVolunteerShiftHourAndMinute(shift.end);
  const effectiveEndStr = projected
    ? getVolunteerShiftHourAndMinute(projected.effectiveEnd)
    : endStr;
  const dayStr = formatVolunteerShiftDay(shift.start);

  return (
    <StyledModal isOpen={open} onClose={onCancel} width="480px" height="auto">
      <Text type="h3">Confirm shift signup</Text>
      <Spacer height={16} />
      <Text type="body1">
        You&apos;re about to register for <strong>{shift.name}</strong>.
      </Text>
      <Spacer height={16} />
      <Summary>
        <SummaryRow>
          <Label>When</Label>
          <Value>
            {dayStr} · {startStr} – {endStr}
          </Value>
        </SummaryRow>
        {shift.location && (
          <SummaryRow>
            <Label>Where</Label>
            <Value>{shift.location}</Value>
          </SummaryRow>
        )}
        <SummaryRow>
          <Label>Your end time</Label>
          <Value>
            {projected?.hasBuffer
              ? `${effectiveEndStr} (includes ${BUFFER_MINUTES}-min buffer)`
              : endStr}
          </Value>
        </SummaryRow>
      </Summary>
      {projected?.hasBuffer && (
        <>
          <Spacer height={12} />
          <Note>
            A {BUFFER_MINUTES}-minute buffer is added after shifts that are not
            back-to-back, so there is sufficient transition time for the shift.
            Scheduling still uses the listed {startStr}–{endStr} window.{" "}
            <BufferLearnMoreLink />
          </Note>
        </>
      )}
      {neighborsLosingBuffer.length > 0 && (
        <>
          <Spacer height={12} />
          <Note>
            Signing up for this shift removes the buffer from{" "}
            {neighborsLosingBuffer.length === 1
              ? "the shift ending right before it"
              : `${neighborsLosingBuffer.length} earlier shifts`}{" "}
            because they become back-to-back.
            {!projected?.hasBuffer && (
              <>
                {" "}
                <BufferLearnMoreLink />
              </>
            )}
          </Note>
        </>
      )}
      <Spacer height={24} />
      <Actions>
        <Button color="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" size="md" onClick={onConfirm}>
          Confirm signup
        </Button>
      </Actions>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  max-height: 90vh;
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const Value = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const Note = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export default SignupConfirmModal;
